import { Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import PrivacyConsentCheckbox from "@/components/forms/PrivacyConsentCheckbox";

/**
 * Visible activation card — used by:
 *   - /activate (the public claim page; logoless variant)
 *   - /parents/claim_child (signed-in parent adding another child;
 *     headless variant rendered inside ParentLayout)
 *   - the help centre, as a non-interactive "screenshot"
 *
 * The wrapper page handles useForm + post; this is purely visual so
 * a help article can drop it in to show "what parents will see."
 *
 * Props (all optional — uncontrolled by default):
 *   claim_code, email, onChange, onSubmit, processing,
 *   errors  — { claim_code, email }
 *   variant — "public" (logo + intro icon, default) | "embedded" (no chrome)
 */
export default function ActivationCard({
  claim_code = "",
  email = "",
  onChange,
  onSubmit,
  processing = false,
  errors = {},
  variant = "public",
  submitLabel,
  showEmail = true,
  showPrivacyConsent = false,
  privacyConsent = false,
}) {
  const isEmbedded = variant === "embedded";
  const finalSubmitLabel = submitLabel || (isEmbedded ? "Find my child" : "Email me my link");
  const submittingLabel = submitLabel ? "Working…" : (isEmbedded ? "Looking up…" : "Sending…");

  const form = (
    <form onSubmit={onSubmit} className="space-y-5">
      <FormField
        name="claim_code"
        label="Activation code"
        required
        autoFocus={!claim_code}
        autoCapitalize="characters"
        placeholder="BEAR-MOON-4217"
        value={claim_code}
        onChange={(e) => onChange?.("claim_code", e.target.value.toUpperCase())}
        error={errors.claim_code}
        help="It's on your child's take-home sheet."
      />
      {showEmail && (
        <FormField
          name="email"
          label="Your email"
          type="email"
          required
          autoFocus={!!claim_code}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => onChange?.("email", e.target.value)}
          error={errors.email}
          help="We'll email you a link to finish. There's no password."
        />
      )}
      {showPrivacyConsent && (
        <PrivacyConsentCheckbox
          checked={privacyConsent}
          onChange={(checked) => onChange?.("privacy_consent", checked)}
          error={errors.privacy_consent}
          id="parent_activation_privacy_consent"
        />
      )}
      <Button type="submit" size="lg" className="w-full" disabled={processing}>
        {processing ? submittingLabel : finalSubmitLabel}
      </Button>
    </form>
  );

  if (isEmbedded) {
    return (
      <Card>
        <CardContent className="p-6 pt-6 md:p-8 md:pt-8">{form}</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <Mail className="h-6 w-6" />
        </div>
        <CardTitle as="h1">Activate your child's reading page</CardTitle>
        <CardDescription>Log their reading and share their page with family.</CardDescription>
      </CardHeader>
      <CardContent>{form}</CardContent>
    </Card>
  );
}
